/**
 * RemoteClient：ApiClient 的远程实现（fetch /v1/paipan/*）
 * 与引擎同源部署（方案 B），无需 CORS；dev 模式由 vite proxy 转发
 *
 * 生产：前端托管于 COS/CDN，引擎在 api.armorclaw.cn，通过 VITE_PAIPAN_API 指定。
 *   默认 `https://api.armorclaw.cn/v1/paipan`（可用构建时环境变量覆盖）；
 *   若与引擎同源部署（如业务机 Go 托管/走 vite proxy），则用相对路径 `/v1/paipan`。
 */
import type { ApiClient, BirthInput, ChartCreated, DaxianResponse, Envelope, LiuriDate, LiuriDaysResponse, LiuriResponse, LiunianResponse, LiushiDate, LiushiResponse, LiuyueResponse, ZizhanCreated, ZizhanRequest } from '../types/api';
import type { Base } from '../types/base';

/** 排盘引擎 API 基础地址：默认同源 /v1/paipan（dev proxy / 同源部署），生产经 VITE_PAIPAN_API 覆盖为 api.armorclaw.cn */
const API_BASE =
  (import.meta.env.VITE_PAIPAN_API as string | undefined)?.replace(/\/$/, '') || '/v1/paipan';

/** 接口错误（信封 success=false 时抛出） */
export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** 请求失败（网络/HTTP 层） */
export class NetworkError extends Error {
  constructor(message: string, public readonly status?: number) {
    super(message);
    this.name = 'NetworkError';
  }
}

/** 拼接引擎 API 全路径 */
function apiUrl(path: string): string {
  return API_BASE + path;
}

async function request<T>(path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (e) {
    throw new NetworkError(`网络请求失败：${e instanceof Error ? e.message : String(e)}`);
  }

  const envelope = (await res.json().catch(() => null)) as Envelope<T> | null;
  if (!envelope || typeof envelope.success !== 'boolean') {
    throw new NetworkError(`响应格式非法（HTTP ${res.status}）`, res.status);
  }
  if (!envelope.success) {
    throw new ApiError(envelope.message || '接口调用失败', res.status);
  }
  if (envelope.data === null) {
    throw new ApiError('响应缺少 data', res.status);
  }
  return envelope.data;
}

export class RemoteClient implements ApiClient {
  createChart(input: BirthInput): Promise<ChartCreated> {
    return request<ChartCreated>('/chart', input);
  }

  zizhan(req: ZizhanRequest): Promise<ZizhanCreated> {
    return request<ZizhanCreated>('/zizhan', req);
  }

  fetchBenming(chartId: string): Promise<Base> {
    return request<Base>(`/chart/${chartId}/benming`);
  }

  fetchDaxian(chartId: string, daxian?: number): Promise<DaxianResponse> {
    return request<DaxianResponse>(`/chart/${chartId}/daxian`, { daxian });
  }

  fetchLiunian(chartId: string, year?: number): Promise<LiunianResponse> {
    return request<LiunianResponse>(`/chart/${chartId}/liunian`, { year });
  }

  fetchLiuyue(chartId: string, year?: number): Promise<LiuyueResponse> {
    return request<LiuyueResponse>(`/chart/${chartId}/liuyue`, { year });
  }

  fetchLiuri(chartId: string, date?: LiuriDate): Promise<LiuriResponse> {
    return request<LiuriResponse>(`/chart/${chartId}/liuri`, date);
  }

  fetchLiuriDays(chartId: string, lunarYear: number, lunarMonth: number): Promise<LiuriDaysResponse> {
    return request<LiuriDaysResponse>(`/chart/${chartId}/liuri/days`, { lunarYear, lunarMonth });
  }

  fetchLiushi(chartId: string, date: LiushiDate): Promise<LiushiResponse> {
    return request<LiushiResponse>(`/chart/${chartId}/liushi`, date);
  }
}
